import { Box, CardActionArea, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { occupancyPercent } from '../lib/format';

export function StatRow({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2 }}>
      {children}
    </Stack>
  );
}

/** A title with one headline number under it. A dash until it has loaded. */
export function StatTile({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value?: string | number | null;
  hint?: string;
  to?: string;
}) {
  const content = (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" component="p" sx={{ fontWeight: 600 }}>
        {value ?? '–'}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </>
  );
  return (
    <Paper variant="outlined" sx={{ flex: '1 1 160px' }}>
      {to ? (
        <CardActionArea component={Link} to={to} sx={{ p: 2, height: '100%' }}>
          {content}
        </CardActionArea>
      ) : (
        <Box sx={{ p: 2 }}>{content}</Box>
      )}
    </Paper>
  );
}

/** Occupied share of rentable units, as a thin bar with the number beside it. */
export function OccupancyMeter({ occupied, vacant }: { occupied: number; vacant: number }) {
  const percent = occupancyPercent(occupied, vacant);
  if (percent === null) return <>–</>;
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 110 }}>
      <LinearProgress
        variant="determinate"
        value={percent}
        aria-label="Occupancy"
        sx={{ flexGrow: 1, height: 6, borderRadius: 1 }}
      />
      <Typography variant="body2">{percent}%</Typography>
    </Stack>
  );
}
